import React, { useEffect, useState } from "react";
import { useNavigate, Navigate } from 'react-router-dom'
import { baseUrl } from "../../shared";
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import Input from '@mui/material/Input';
import Table from '@mui/material/Table';
import TableHead from "@mui/material/TableHead";
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Autocomplete from '@mui/material/Autocomplete';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { MobileDatePicker } from "@mui/x-date-pickers";
import TextField from '@mui/material/TextField';
import dayjs from 'dayjs';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';

const initialRow = {
  catNo: '',
  assetId: '',
  customer: '',
  type: '',
  siteLocation: '',
  item: '',
  manufacturer: '',
  model: '',
  serialNo: '',
  maxHeight: '',
  capacity: '',
  buildDate: '',
  calibrationExpiryDate: '',
};

const emptyRow = {
  catNo: '',
  assetId: '',
  customer: '',
  type: '',
  siteLocation: '',
  item: '',
  manufacturer: '',
  model: '',
  serialNo: '',
  maxHeight: '',
  capacity: '',
  buildDate: '',
  calibrationExpiryDate: ''
};

const getEmptyRow = () => ({
  catNo: '',
  assetId: '',
  customer: '',
  type: '',
  siteLocation: '',
  item: '',
  manufacturer: '',
  model: '',
  serialNo: '',
  maxHeight: '',
  capacity: '',
  buildDate: '',
  calibrationExpiryDate: '',
});


export default function AssetsBulk({ companies, assetTypes, siteLocations, descriptions, makes, models, maxHeights, capacities }) {
  var [assetTypes, setAssetTypes] = useState([]);
  const [assetTypesLoaded, setAssetTypesLoaded] = useState(false);
  var [companies, setCompanies] = useState([]);
  const [companiesLoaded, setCompaniesLoaded] = useState(false);
  var [capacities, setCapacities] = useState([]);
  const [capacitiesLoaded, setCapacitiesLoaded] = useState(false);
  var [descriptions, setDescriptions] = useState([]);
  const [descriptionsLoaded, setDescriptionsLoaded] = useState(false);
  var [makes, setMakes] = useState([]);
  const [makesLoaded, setMakesLoaded] = useState(false);
  var [maxHeights, setMaxHeights] = useState([]);
  const [maxHeightsLoaded, setMaxHeightsLoaded] = useState(false);
  var [models, setModels] = useState([]);
  const [modelsLoaded, setModelsLoaded] = useState(false);
  var [siteLocations, setSiteLocations] = useState([]);
  const [siteLocationsLoaded, setSiteLocationsLoaded] = useState(false);
  var [companyAcId, setCompanyAcId] = useState();
  const [rows, setRows] = useState([initialRow]);
  const [rowErrors, setRowErrors] = useState([]);

  const handleChange = (index, field, value) => {
    const newRows = [...rows];
    newRows[index][field] =
      (field === 'buildDate' || field === 'calibrationExpiryDate') && value !== ''
        ? dayjs(value).format('YYYY-MM-DD')
        : value;
    setRows(newRows);
  };

  const addRow = () => {
    setRows([...rows, { ...emptyRow }]);
  };

  const fieldOrder = [
    'catNo', 'assetId', 'customer', 'type', 'siteLocation', 'item',
    'manufacturer', 'model', 'serialNo', 'maxHeight', 'capacity',
    'buildDate', 'calibrationExpiryDate'
  ];


  const handleRemoveRow = (indexToRemove) => {
    setRows(prevRows => {
      const newRows = prevRows.filter((_, index) => index !== indexToRemove);

      if (newRows.length === 0) {
        return [getEmptyRow()]; // ⬅️ Replace with empty row if last one deleted
      }

      return newRows;
    });

    setRowErrors(prevErrors => {
      const newErrors = prevErrors.filter((_, index) => index !== indexToRemove);

      if (newErrors.length === 0) {
        return [{}]; // ⬅️ Also reset errors
      }

      return newErrors;
    });
  };


  const handlePaste = (event) => {
    event.preventDefault();
    const pastedText = event.clipboardData.getData('text/plain');
    const rows = pastedText.trim().split('\n').map(row => row.split('\t'));

    let hasHeader = false;
    const firstRow = rows[0].map(cell => cell.toLowerCase().replace(/\s+/g, ''));

    // Detect header row
    const expectedHeaders = fieldOrder.map(f => f.toLowerCase().replace(/[^a-z]/g, ''));
    const headerMatchCount = firstRow.filter(cell => expectedHeaders.includes(cell)).length;

    if (headerMatchCount >= 3) {
      hasHeader = true;
    }

    const dataRows = hasHeader ? rows.slice(1) : rows;

    const newRows = dataRows.map((cols) => {
      const rowObj = {};

      fieldOrder.forEach((key, idx) => {
        let val = cols[idx] || '';

        // Format date fields
        if (['buildDate', 'calibrationExpiryDate'].includes(key) && val) {
          const parsed = dayjs(val);
          if (parsed.isValid()) {
            val = parsed.format('YYYY-MM-DD');
          } else {
            val = '';
          }
        }

        rowObj[key] = val.toUpperCase();
      });

      return rowObj;
    });

    setRows(prev => [...prev, ...newRows]);
  };

  const handleFieldChange = (index, field, value) => {
    setRows(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value.value };
      return updated;
    });

    validateSingleField(index, field, value);
  };

  function validateAllRows(rows) {
  const newErrors = [];

  // Bare minimum to import a new asset: Cat No, Asset ID and a valid Customer.
  // Everything else is optional — but if a value IS entered it must match a known
  // reference value. Blank optional fields import as '-' (backend default) and can
  // be filled in later.
  rows.forEach((row, index) => {
    const rowErrors = {};

    if (!row.catNo?.trim()) {
      rowErrors.catNo = 'Required';
    }

    if (!row.assetId?.trim()) {
      rowErrors.assetId = 'Required';
    }

    if (!companies.some(c => c.value === row.customer)) {
      rowErrors.customer = 'Invalid customer';
    }

    // Optional reference fields — only validate when the user actually entered something.
    if (row.type?.trim() && !assetTypes.some(t => t.value === row.type)) {
      rowErrors.type = 'Invalid type';
    }

    if (row.siteLocation?.trim() && !siteLocations.some(s => s.value === row.siteLocation)) {
      rowErrors.siteLocation = 'Invalid site location';
    }

    if (row.item?.trim() && !descriptions.some(d => d.value === row.item)) {
      rowErrors.item = 'Invalid item';
    }

    if (row.manufacturer?.trim() && !makes.some(m => m.value === row.manufacturer)) {
      rowErrors.manufacturer = 'Invalid manufacturer';
    }

    if (row.model?.trim() && !models.some(m => m.value === row.model)) {
      rowErrors.model = 'Invalid model';
    }

    if (row.maxHeight?.trim() && !maxHeights.some(h => h.value === row.maxHeight)) {
      rowErrors.maxHeight = 'Invalid max height';
    }

    if (row.capacity?.trim() && !capacities.some(c => c.value === row.capacity)) {
      rowErrors.capacity = 'Invalid capacity';
    }

    newErrors.push(rowErrors);
  });

  setRowErrors(newErrors);

  return newErrors.every((e) => Object.keys(e).length === 0);
}




  const validateSingleField = (index, field, value) => {
    const validLists = {
      customer: companies,
      type: assetTypes,
      siteLocation: siteLocations,
      item: descriptions,
      manufacturer: makes,
      model: models,
      maxHeight: maxHeights,
      capacity: capacities,
    };

    const isValid = validLists[field]
      ? validLists[field].includes(value)
      : !!value?.trim(); 


    setRowErrors(prev => {
      const updated = [...prev];
      const row = { ...(updated[index] || {}) };

      if (isValid) {
        delete row[field];
      } else {
        row[field] = 'Invalid value';
      }

      updated[index] = row;
      return updated;
    });
  };

  const handleSubmit = () => {

    const allValid = validateAllRows(rows);

    if (!allValid) {
      alert('Some rows have invalid values. Please fix them first.');
      console.log("Here: ",rowErrors)
      return;
    }
    const errors = [];
    // Only the bare-minimum fields must be present; everything else can be left blank
    // and will import as '-' (or null for dates) to be updated later.
    const requiredFields = ['catNo', 'assetId', 'customer'];

    rows.forEach((row, index) => {
      const missingFields = requiredFields.filter((key) => {
        const value = row[key];
        return value === '' || value === null || value === undefined;
      });

      if (missingFields.length > 0) {
        errors.push({
          row: index + 1,
          fields: missingFields,
        });
      }
    });

    if (errors.length > 0) {
      console.warn('Validation errors:', errors);

      alert(
        `Please fill all fields.\n\n` +
        errors.map(e => `Row ${e.row}: Missing ${e.fields.join(', ')}`).join('\n')
      );

      return;
    }
    uploadBulkAssets(rows);
  };

async function uploadBulkAssets(e) {
  console.log('rows: ', rows)
  console.log('e: ',e)
    const url = baseUrl + "upload/assets";
    await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': "application/json",
        'username': localStorage.getItem('username'),
        'bToken': localStorage.getItem('bToken')
      },
      body: JSON.stringify(rows),
    })
      .then((response) => response.json())
      .then((data) => {
        console.log('result: ',data)
        if (data.status === 200) {
          setMessage(data.message);
          setSucCount(data.successCount);
          setErrCount(data.errors);
          setOpen(true);
        } else if (data.status === 207) {
          setMessage(data.message);
          setSucCount(data.successCount);
          setErrCount(data.errors);
          setOpen(true);
        } else if (data.status === 401) {
          setMessage(data);
          setOpen(true);
        }
      })
      .catch((err) => {
        setMessage(err.message);
        setOpen(true);
      });
  }

  const loadAssetTypes = async () => {
    const url = baseUrl + 'asset/typeAc/';
    await fetch(url, {
      method: 'GET',
      headers: {
        'username': localStorage.getItem('username'),
        'bToken': localStorage.getItem('bToken')
      }
    })
      .then((res) => {
        return res.json();
      }).then((json) => {
        setAssetTypes(json);
        console.log('Types Loaded ', assetTypes)
      })
      .catch((err) => {
        setAssetTypesLoaded(false);
        console.log(err.message);
      });
  }

  const loadCompanies = async () => {
    const url = baseUrl + 'company/listAc/';
    await fetch(url, {
      method: 'GET',
      headers: {
        'username': localStorage.getItem('username'),
        'bToken': localStorage.getItem('bToken')
      }
    })
      .then((res) => {
        return res.json();
      }).then((json) => {
        console.log('companies ', json)
        setCompanies(json);
        setCompanyAcId(`${Math.floor((Math.random() * 1000))}-min`);
        console.log('companies named ', companies)
      })
      .catch((err) => {
        setCompaniesLoaded(false);
        console.log(err.message);
      });
  }

  const loadCapacities = async () => {
    const url = baseUrl + 'asset/capacityAc/';
    await fetch(url, {
      method: 'GET',
      headers: {
        'username': localStorage.getItem('username'),
        'bToken': localStorage.getItem('bToken')
      }
    })
      .then((res) => {
        return res.json();
      }).then((json) => {
        console.log(json)
        setCapacities(json);
      })
      .catch((err) => {
        setCapacitiesLoaded(false);
        console.log(err.message);
      });
  }

  const loadDescriptions = async () => {
    const url = baseUrl + 'asset/descriptionAc/';
    await fetch(url, {
      method: 'GET',
      headers: {
        'username': localStorage.getItem('username'),
        'bToken': localStorage.getItem('bToken')
      }
    })
      .then((res) => {
        return res.json();
      }).then((json) => {
        console.log(json)
        setDescriptions(json);
      })
      .catch((err) => {
        setDescriptionsLoaded(false);
        console.log(err.message);
      });
  }

  const loadMakes = async () => {
    const url = baseUrl + 'asset/makeAc/';
    await fetch(url, {
      method: 'GET',
      headers: {
        'username': localStorage.getItem('username'),
        'bToken': localStorage.getItem('bToken')
      }
    })
      .then((res) => {
        return res.json();
      }).then((json) => {
        console.log(json)
        setMakes(json);
      })
      .catch((err) => {
        setMakesLoaded(false);
        console.log(err.message);
      });
  }

  const loadMaxHeights = async () => {
    const url = baseUrl + 'asset/maxHeightAc/';
    await fetch(url, {
      method: 'GET',
      headers: {
        'username': localStorage.getItem('username'),
        'bToken': localStorage.getItem('bToken')
      }
    })
      .then((res) => {
        return res.json();
      }).then((json) => {
        console.log(json)
        setMaxHeights(json);
      })
      .catch((err) => {
        setMaxHeightsLoaded(false);
        console.log(err.message);
      });
  }

  const loadSiteLocations = async () => {
    const url = baseUrl + 'asset/siteLocationAc/';
    await fetch(url, {
      method: 'GET',
      headers: {
        'username': localStorage.getItem('username'),
        'bToken': localStorage.getItem('bToken')
      }
    })
      .then((res) => {
        return res.json();
      }).then((json) => {
        console.log(json)
        setSiteLocations(json);
      })
      .catch((err) => {
        setSiteLocationsLoaded(false);
        console.log(err.message);
      });
  }

  const loadModels = async () => {
    const url = baseUrl + 'asset/modelAc/';
    await fetch(url, {
      method: 'GET',
      headers: {
        'username': localStorage.getItem('username'),
        'bToken': localStorage.getItem('bToken')
      }
    })
      .then((res) => {
        return res.json();
      }).then((json) => {
        console.log(json)
        setModels(json);
      })
      .catch((err) => {
        setModelsLoaded(false);
        console.log(err.message);
      });
  }

  useEffect(() => {
    if (!assetTypesLoaded) {
      setAssetTypesLoaded(true)
      loadAssetTypes()
    }
    if (!companiesLoaded) {
      setCompaniesLoaded(true)
      loadCompanies()
    }
    if (!capacitiesLoaded) {
      setCapacitiesLoaded(true)
      loadCapacities()
    }
    if (!descriptionsLoaded) {
      setDescriptionsLoaded(true)
      loadDescriptions()
    }
    if (!makesLoaded) {
      setMakesLoaded(true)
      loadMakes()
    }
    if (!maxHeightsLoaded) {
      setMaxHeightsLoaded(true)
      loadMaxHeights()
    }
    if (!siteLocationsLoaded) {
      setSiteLocationsLoaded(true)
      loadSiteLocations()
    }
    if (!modelsLoaded) {
      setModelsLoaded(true)
      loadModels()
    }
  })

  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [errCount, setErrCount] = useState('');
  const [sucCount, setSucCount] = useState({});
  const navigate = useNavigate();

  const handleClose = () => {
    <Navigate to={'/admin/'} />
    { navigate(0) }
  };

  return (
    <div className="bulkAsset">
      <React.Fragment>
        <Dialog
          open={open}
          onClose={handleClose}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle>
            Assets Bulk Upload
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              {message}
            </DialogContentText>
            <DialogContentText>
              Successfully Created: {sucCount}
            </DialogContentText>
            <DialogContentText>
              {`Errors: ${errCount.length}`}
            </DialogContentText>

            {errCount.length > 0 && (
              <ul>
                {errCount.map((e, idx) => (
                  <li key={idx}>{e.assetId}: {e.error}</li>
                ))}
              </ul>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Ok</Button>
          </DialogActions>
        </Dialog>
      </React.Fragment>
      <>
        <TextField
          label="Paste rows from Excel here"
          variant="outlined"
          onPaste={handlePaste}
          placeholder="Paste data with or without headers"
          fullWidth
          sx={{ mb: 2 }}
          multiline
        />
        <TableContainer component={Paper}>
          <Table aria-label="simple table" sx={{ width: '100%' }}>
            <TableHead>
              <TableRow>
                <TableCell>
                  <IconButton onClick={() => addRow()}><AddCircleOutlineOutlinedIcon /></IconButton>
                </TableCell>
                {[
                  'Catalogue Number', 'Asset Number', 'Customer', 'Type', 'Site Location',
                  'Item', 'Manufacturer', 'Model', 'Serial Number', 'Max Height',
                  'Capacity', 'Build Date', 'Calibration Expiry Date'
                ].map((header) => (
                  <TableCell key={header} sx={{ fontSize: '0.8rem' }}>{header}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <IconButton onClick={() => handleRemoveRow(index)} aria-label="delete">
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                  <TableCell>
                    <Input
                      style={{ width: '175px', blockSize: '0.8rem' }}
                      value={row.catNo}
                      onChange={(e) => handleChange(index, 'catNo', e.target.value)}
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      style={{ width: '175px', blockSize: '0.8rem' }}
                      value={row.assetId}
                      onChange={(e) => handleChange(index, 'assetId', e.target.value)}
                    />
                  </TableCell>
                  <TableCell>
                    <Autocomplete
                      disablePortal
                      disableClearable
                      options={companies}
                      value={companies.find((opt) => opt.value === row.customer) || null}
                      onChange={(e, newValue) => {
                        const selectedValue = newValue || '';
                        handleFieldChange(index, 'customer', selectedValue);
                      }}
                      isOptionEqualToValue={(option, value) =>
                        option?.value === value?.value
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          error={!!rowErrors[index]?.customer}
                        />
                      )}
                      style={{ width: '175px', fontSize: '0.8rem' }}
                    />
                  </TableCell>
                  <TableCell>
                    <Autocomplete
                      disablePortal disableClearable
                      options={assetTypes}
                      value={assetTypes.find((opt) => opt.value === row.type) || null}
                      onChange={(e, newValue) => {
                        const selectedValue = newValue || '';
                        handleFieldChange(index, 'type', selectedValue);
                      }}
                      isOptionEqualToValue={(option, value) =>
                        option?.value === value?.value
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          error={!!rowErrors[index]?.type}
                        />
                      )}
                      style={{ width: '175px', fontSize: '0.8rem' }}
                    />
                  </TableCell>
                  <TableCell>
                    <Autocomplete
                      disablePortal disableClearable
                      options={siteLocations}
                      value={siteLocations.find((opt) => opt.value === row.siteLocation) || null}
                      onChange={(e, newValue) => {
                        const selectedValue = newValue || '';
                        handleFieldChange(index, 'siteLocation', selectedValue);
                      }}
                      isOptionEqualToValue={(option, value) =>
                        option?.value === value?.value
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          error={!!rowErrors[index]?.siteLocation}
                        />
                      )}
                      style={{ width: '175px', fontSize: '0.8rem' }}
                    />
                  </TableCell>
                  <TableCell>
                    <Autocomplete
                      disablePortal disableClearable
                      options={descriptions}
                      value={descriptions.find((opt) => opt.value === row.item) || null}
                      onChange={(e, newValue) => {
                        const selectedValue = newValue || '';
                        handleFieldChange(index, 'item', selectedValue);
                      }}
                      isOptionEqualToValue={(option, value) =>
                        option?.value === value?.value
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          error={!!rowErrors[index]?.item}
                        />
                      )}
                      style={{ width: '175px', fontSize: '0.8rem' }}
                    />
                  </TableCell>
                  <TableCell>
                    <Autocomplete
                      disablePortal disableClearable
                      options={makes}
                      value={makes.find((opt) => opt.value === row.manufacturer) || null}
                      onChange={(e, newValue) => {
                        const selectedValue = newValue || '';
                        handleFieldChange(index, 'manufacturer', selectedValue);
                      }}
                      isOptionEqualToValue={(option, value) =>
                        option?.value === value?.value
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          error={!!rowErrors[index]?.manufacturer}
                        />
                      )}
                      style={{ width: '175px', fontSize: '0.8rem' }}
                    />
                  </TableCell>
                  <TableCell>
                    <Autocomplete
                      disablePortal disableClearable
                      options={models}
                      value={models.find((opt) => opt.value === row.model) || null}
                      onChange={(e, newValue) => {
                        const selectedValue = newValue || '';
                        handleFieldChange(index, 'model', selectedValue);
                      }}
                      isOptionEqualToValue={(option, value) =>
                        option?.value === value?.value
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          error={!!rowErrors[index]?.model}
                        />
                      )}
                      style={{ width: '175px', fontSize: '0.8rem' }}
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      style={{ width: '175px', blockSize: '0.8rem' }}
                      value={row.serialNo}
                      onChange={(e) => handleChange(index, 'serialNo', e.target.value)}
                    />
                  </TableCell>
                  <TableCell>
                    <Autocomplete
                      disablePortal disableClearable
                      options={maxHeights}
                      value={maxHeights.find((opt) => opt.value === row.maxHeight) || null}
                      onChange={(e, newValue) => {
                        const selectedValue = newValue || '';
                        handleFieldChange(index, 'maxHeight', selectedValue);
                      }}
                      isOptionEqualToValue={(option, value) =>
                        option?.value === value?.value
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          error={!!rowErrors[index]?.maxHeight}
                        />
                      )}
                      style={{ width: '175px', fontSize: '0.8rem' }}
                    />
                  </TableCell>
                  <TableCell>
                    <Autocomplete
                      disablePortal disableClearable
                      options={capacities}
                      value={capacities.find((opt) => opt.value === row.capacity) || null}
                      onChange={(e, newValue) => {
                        const selectedValue = newValue || '';
                        handleFieldChange(index, 'capacity', selectedValue);
                      }}
                      isOptionEqualToValue={(option, value) =>
                        option?.value === value?.value
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          error={!!rowErrors[index]?.capacity}
                        />
                      )}
                      style={{ width: '175px', fontSize: '0.8rem' }}
                    />
                  </TableCell>
                  <TableCell>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <MobileDatePicker
                        sx={{ mt: 1, mb: 1, width: '175px' }}
                        value={dayjs(row.buildDate, 'YYYY-MM-DD')}
                        onChange={(newValue) => handleChange(index, 'buildDate', newValue)}
                        format="DD-MM-YYYY"
                      />
                    </LocalizationProvider>
                  </TableCell>
                  <TableCell>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <MobileDatePicker
                        sx={{ mt: 1, mb: 1, width: '175px' }}
                        value={dayjs(row.calibrationExpiryDate, 'YYYY-MM-DD')}
                        onChange={(newValue) => handleChange(index, 'calibrationExpiryDate', (dayjs(newValue.$d) === 'Invalid date') ? '-' : dayjs(newValue.$d))}
                        format="DD-MM-YYYY"
                      />
                    </LocalizationProvider>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </>
      <>
        <Button onClick={handleSubmit} variant="contained" style={{ marginLeft: 'auto', display: 'block' }}>Submit</Button>
      </>
    </div>
  );
}